/**
 * Privacy Policy page component for Stack Forward website
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * PrivacyPolicy component
 */
export const PrivacyPolicy: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="container-custom">
          <div className="py-4">
            <Link
              to="/"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="py-16">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {/* Page title */}
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-8">
              Privacy Policy
            </h1>

            <div className="prose prose-lg max-w-none">
              {/* Last updated */}
              <p className="text-neutral-600 mb-8">
                <strong>Last Updated:</strong>{' '}
                {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>

              {/* Introduction */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  1. Introduction
                </h2>
                <p className="text-neutral-700 mb-4">
                  Stack Forward ("we," "our," or "us") is committed to
                  protecting your privacy. This Privacy Policy explains how we
                  collect, use, disclose, and safeguard your information when
                  you visit our website at stackforward.org and use our
                  services.
                </p>
                <p className="text-neutral-700">
                  By using our website or services, you agree to the collection
                  and use of information in accordance with this policy.
                </p>
              </section>

              {/* Information we collect */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  2. Information We Collect
                </h2>

                <h3 className="text-xl font-semibold text-neutral-900 mb-3">
                  2.1 Information You Provide
                </h3>
                <p className="text-neutral-700 mb-4">
                  When you contact us through our website, we may collect:
                </p>
                <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number (optional)</li>
                  <li>Company name (optional)</li>
                  <li>Areas of interest in open-source solutions</li>
                  <li>Message content</li>
                </ul>

                <h3 className="text-xl font-semibold text-neutral-900 mb-3">
                  2.2 Automatically Collected Information
                </h3>
                <p className="text-neutral-700 mb-4">
                  We may automatically collect certain information about your
                  device and how you interact with our website, including:
                </p>
                <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Referral source (UTM parameters)</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Device information</li>
                </ul>
              </section>

              {/* How we use information */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  3. How We Use Your Information
                </h2>
                <p className="text-neutral-700 mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                  <li>
                    Respond to your inquiries and provide customer support
                  </li>
                  <li>Communicate with you about our services</li>
                  <li>Improve our website and services</li>
                  <li>Analyze usage patterns and trends</li>
                  <li>Prevent fraud and ensure security</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              {/* Information sharing */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  4. Information Sharing and Disclosure
                </h2>
                <p className="text-neutral-700 mb-4">
                  We do not sell, trade, or rent your personal information to
                  third parties. We may share your information only in the
                  following circumstances:
                </p>
                <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                  <li>
                    <strong>Service Providers:</strong> With trusted third-party
                    service providers who assist us in operating our website and
                    providing services (e.g., hosting, email delivery,
                    analytics)
                  </li>
                  <li>
                    <strong>Legal Requirements:</strong> When required by law,
                    regulation, legal process, or governmental request
                  </li>
                  <li>
                    <strong>Business Transfers:</strong> In connection with any
                    merger, sale of company assets, financing, or acquisition of
                    all or a portion of our business
                  </li>
                  <li>
                    <strong>With Your Consent:</strong> When you explicitly
                    consent to the sharing of your information
                  </li>
                </ul>
              </section>

              {/* Data retention */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  5. Data Retention
                </h2>
                <p className="text-neutral-700">
                  We retain your personal information only for as long as
                  necessary to fulfill the purposes outlined in this Privacy
                  Policy, unless a longer retention period is required or
                  permitted by law. When we no longer need your information, we
                  will securely delete or anonymize it.
                </p>
              </section>

              {/* Data security */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  6. Data Security
                </h2>
                <p className="text-neutral-700 mb-4">
                  We implement appropriate technical and organizational security
                  measures to protect your personal information against
                  unauthorized access, alteration, disclosure, or destruction.
                  These measures include:
                </p>
                <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments</li>
                  <li>Access controls and authentication</li>
                  <li>Secure hosting infrastructure</li>
                </ul>
                <p className="text-neutral-700">
                  However, no method of transmission over the Internet or
                  electronic storage is 100% secure. While we strive to protect
                  your personal information, we cannot guarantee its absolute
                  security.
                </p>
              </section>

              {/* Your rights */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  7. Your Privacy Rights
                </h2>
                <p className="text-neutral-700 mb-4">
                  Depending on your location, you may have certain rights
                  regarding your personal information, including:
                </p>
                <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                  <li>
                    <strong>Access:</strong> Request access to the personal
                    information we hold about you
                  </li>
                  <li>
                    <strong>Correction:</strong> Request correction of
                    inaccurate or incomplete information
                  </li>
                  <li>
                    <strong>Deletion:</strong> Request deletion of your personal
                    information
                  </li>
                  <li>
                    <strong>Objection:</strong> Object to the processing of your
                    personal information
                  </li>
                  <li>
                    <strong>Data Portability:</strong> Request a copy of your
                    data in a structured, machine-readable format
                  </li>
                  <li>
                    <strong>Withdraw Consent:</strong> Withdraw your consent at
                    any time where we rely on consent to process your
                    information
                  </li>
                </ul>
                <p className="text-neutral-700">
                  To exercise any of these rights, please contact us at{' '}
                  <a
                    href="mailto:hello@stackforward.org"
                    className="text-primary-600 hover:text-primary-700 underline"
                  >
                    hello@stackforward.org
                  </a>
                </p>
              </section>

              {/* Cookies */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  8. Cookies and Tracking Technologies
                </h2>
                <p className="text-neutral-700 mb-4">
                  We may use cookies and similar tracking technologies to track
                  activity on our website and store certain information. You can
                  instruct your browser to refuse all cookies or to indicate
                  when a cookie is being sent. However, if you do not accept
                  cookies, you may not be able to use some portions of our
                  website.
                </p>
              </section>

              {/* Third-party links */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  9. Third-Party Links
                </h2>
                <p className="text-neutral-700">
                  Our website may contain links to third-party websites. We are
                  not responsible for the privacy practices or content of these
                  third-party sites. We encourage you to read the privacy
                  policies of any third-party sites you visit.
                </p>
              </section>

              {/* Children's privacy */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  10. Children's Privacy
                </h2>
                <p className="text-neutral-700">
                  Our services are not intended for individuals under the age of
                  18. We do not knowingly collect personal information from
                  children under 18. If you are a parent or guardian and believe
                  your child has provided us with personal information, please
                  contact us.
                </p>
              </section>

              {/* International transfers */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  11. International Data Transfers
                </h2>
                <p className="text-neutral-700">
                  Your information may be transferred to and processed in
                  countries other than your country of residence. These
                  countries may have data protection laws that are different
                  from the laws of your country. We take appropriate safeguards
                  to ensure that your personal information remains protected in
                  accordance with this Privacy Policy.
                </p>
              </section>

              {/* Changes to policy */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  12. Changes to This Privacy Policy
                </h2>
                <p className="text-neutral-700">
                  We may update this Privacy Policy from time to time. We will
                  notify you of any changes by posting the new Privacy Policy on
                  this page and updating the "Last Updated" date. You are
                  advised to review this Privacy Policy periodically for any
                  changes.
                </p>
              </section>

              {/* Contact */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                  13. Contact Us
                </h2>
                <p className="text-neutral-700 mb-4">
                  If you have any questions about this Privacy Policy or our
                  data practices, please contact us:
                </p>
                <div className="bg-neutral-50 p-6 rounded-lg">
                  <p className="text-neutral-900 font-semibold mb-2">
                    Stack Forward
                  </p>
                  <p className="text-neutral-700">
                    Email:{' '}
                    <a
                      href="mailto:hello@stackforward.org"
                      className="text-primary-600 hover:text-primary-700 underline"
                    >
                      hello@stackforward.org
                    </a>
                  </p>
                  <p className="text-neutral-700">
                    Website:{' '}
                    <Link
                      to="/"
                      className="text-primary-600 hover:text-primary-700 underline"
                    >
                      stackforward.org
                    </Link>
                  </p>
                </div>
              </section>
            </div>

            {/* Back to home link at bottom */}
            <div className="mt-12 pt-8 border-t border-neutral-200">
              <Link
                to="/"
                className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200 font-medium"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
